import { useState, type FormEvent } from "react";
import type { ChapterDetail, CreateVocabWordBody, TargetWord, VocabKind, VocabWord } from "@storyai/shared";
import { useTargetWordMutations, useVocabMutations } from "../../lib/queries";
import { Field, InlineConfirm, MutationError } from "../ui";

// ---- Vocabulary -----------------------------------------------------------------

interface VocabForm {
  word: string;
  kind: VocabKind;
  meaning: string;
  d1: string;
  d2: string;
  example: string;
  formsText: string;
}

const EMPTY_VOCAB: VocabForm = { word: "", kind: "passage", meaning: "", d1: "", d2: "", example: "", formsText: "" };

function vocabToForm(v: VocabWord): VocabForm {
  return {
    word: v.word,
    kind: v.kind,
    meaning: v.meaning,
    d1: v.distractors[0],
    d2: v.distractors[1],
    example: v.example,
    formsText: v.forms.join(", "),
  };
}

function parseForms(text: string): string[] | undefined {
  const forms = text
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  return forms.length ? Array.from(new Set(forms)) : undefined;
}

function vocabToBody(f: VocabForm, includeForms: boolean): CreateVocabWordBody {
  const body: CreateVocabWordBody = {
    word: f.word.trim(),
    kind: f.kind,
    meaning: f.meaning.trim(),
    distractors: [f.d1.trim(), f.d2.trim()],
    example: f.example.trim() || undefined,
  };
  if (includeForms) {
    const forms = parseForms(f.formsText);
    if (forms) body.forms = forms;
  }
  return body;
}

function VocabFields({ value, onChange, showForms }: { value: VocabForm; onChange: (f: VocabForm) => void; showForms: boolean }) {
  const set = <K extends keyof VocabForm>(k: K, v: VocabForm[K]) => onChange({ ...value, [k]: v });
  return (
    <div className="form">
      <div className="form-grid">
        <Field label="Word">
          <input type="text" value={value.word} required maxLength={60} onChange={(e) => set("word", e.target.value)} />
        </Field>
        <fieldset className="radio-group">
          <legend className="field-label">Where it appears</legend>
          <label className="radio">
            <input type="radio" checked={value.kind === "passage"} onChange={() => set("kind", "passage")} />
            <span>
              <span className="radio-label">Passage</span>
              <span className="muted small"> In this chapter's description; the card shows before the chat opens.</span>
            </span>
          </label>
          <label className="radio">
            <input type="radio" checked={value.kind === "woven"} onChange={() => set("kind", "woven")} />
            <span>
              <span className="radio-label">Woven</span>
              <span className="muted small"> The game master may use it in narration; the card shows under that reply.</span>
            </span>
          </label>
        </fieldset>
      </div>
      <Field label="Meaning in this passage" hint="The correct choice.">
        <input type="text" value={value.meaning} required maxLength={300} onChange={(e) => set("meaning", e.target.value)} />
      </Field>
      <div className="form-grid">
        <Field label="Wrong choice 1">
          <input type="text" value={value.d1} required maxLength={300} onChange={(e) => set("d1", e.target.value)} />
        </Field>
        <Field label="Wrong choice 2">
          <input type="text" value={value.d2} required maxLength={300} onChange={(e) => set("d2", e.target.value)} />
        </Field>
      </div>
      <Field label="Example sentence" hint="Optional.">
        <input type="text" value={value.example} maxLength={300} onChange={(e) => set("example", e.target.value)} />
      </Field>
      {showForms && (
        <Field label="Accepted forms" hint="Comma-separated. Generated when the word was added; edit if needed.">
          <input type="text" className="mono" value={value.formsText} onChange={(e) => set("formsText", e.target.value)} />
        </Field>
      )}
    </div>
  );
}

export function VocabularySection({ storyId, chapter, compact }: { storyId: string; chapter: ChapterDetail; compact: boolean }) {
  const { create, update, remove } = useVocabMutations(storyId, chapter.id);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<VocabForm>(EMPTY_VOCAB);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [edit, setEdit] = useState<VocabForm>(EMPTY_VOCAB);

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    await create.mutateAsync(vocabToBody(draft, false));
    setDraft(EMPTY_VOCAB);
    setAdding(false);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    await update.mutateAsync({ id: editingId, body: vocabToBody(edit, true) });
    setEditingId(null);
  }

  const words = [...chapter.vocabWords].sort((a, b) => a.sortOrder - b.sortOrder);
  const descLower = chapter.description.toLowerCase();
  const missing = (v: VocabWord) => v.kind === "passage" && !v.forms.some((f) => descLower.includes(f.toLowerCase()));

  return (
    <section className="section">
      <div className="section-head">
        <h2>Vocabulary</h2>
        {!adding && (
          <button type="button" className="btn btn-sm" onClick={() => setAdding(true)}>
            Add word
          </button>
        )}
      </div>
      <p className="muted small">
        Each word gets a multiple-choice card. Passage words must appear in the chapter description; woven words are
        offered to the game master to use in narration.
      </p>
      {words.length === 0 && !adding && <p className="muted small">No vocabulary yet.</p>}
      {words.length > 0 && (
        <ul className="stack-list">
          {words.map((v) =>
            editingId === v.id ? (
              <li key={v.id} className="stack-item">
                <form className="stack-item-main" onSubmit={onSave}>
                  <VocabFields value={edit} onChange={setEdit} showForms />
                  <div className="form-actions">
                    <button type="submit" className="btn btn-sm btn-primary" disabled={update.isPending}>
                      Save
                    </button>
                    <button type="button" className="btn btn-sm" onClick={() => setEditingId(null)}>
                      Cancel
                    </button>
                  </div>
                </form>
              </li>
            ) : (
              <li key={v.id} className="stack-item">
                <div className="stack-item-main">
                  <div className="stack-item-title">
                    <span className={`vocab-kind ${v.kind}`}>{v.kind}</span>
                    {v.word}
                    {missing(v) && (
                      <span className="field-error"> — not found in the description</span>
                    )}
                  </div>
                  <div className="small">{v.meaning}</div>
                  <div className="forms-text">forms: {v.forms.join(", ")}</div>
                </div>
                <div className="stack-item-actions">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      setEdit(vocabToForm(v));
                      setEditingId(v.id);
                    }}
                  >
                    Edit
                  </button>
                  <InlineConfirm label="Delete" onConfirm={() => remove.mutate(v.id)} busy={remove.isPending} />
                </div>
              </li>
            ),
          )}
        </ul>
      )}
      {adding && (
        <form className={compact ? "stack-item" : "card"} onSubmit={onAdd}>
          <VocabFields value={draft} onChange={setDraft} showForms={false} />
          <div className="form-actions">
            <button type="submit" className="btn btn-sm btn-primary" disabled={create.isPending}>
              Add word
            </button>
            <button type="button" className="btn btn-sm" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
      <MutationError error={create.error ?? update.error ?? remove.error} />
    </section>
  );
}

// ---- Target words ---------------------------------------------------------------

export function TargetWordsSection({ storyId, chapter }: { storyId: string; chapter: ChapterDetail }) {
  const { create, update, remove } = useTargetWordMutations(storyId, chapter.id);
  const [word, setWord] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editWord, setEditWord] = useState("");
  const [editForms, setEditForms] = useState("");

  async function onAdd(e: FormEvent) {
    e.preventDefault();
    await create.mutateAsync({ word: word.trim() });
    setWord("");
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    await update.mutateAsync({ id: editingId, body: { word: editWord.trim(), forms: parseForms(editForms) } });
    setEditingId(null);
  }

  const words: TargetWord[] = [...chapter.targetWords].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <section className="section">
      <h2>Target words</h2>
      <p className="muted small">
        Words the player must use in their own writing before this chapter's exits will fire. Accepted forms are
        generated when a word is added; edit them if needed.
      </p>
      {words.length > 0 && (
        <ul className="stack-list">
          {words.map((t) =>
            editingId === t.id ? (
              <li key={t.id} className="stack-item">
                <form className="stack-item-main form" onSubmit={onSave}>
                  <div className="form-grid">
                    <Field label="Word">
                      <input type="text" value={editWord} required maxLength={60} onChange={(e) => setEditWord(e.target.value)} />
                    </Field>
                    <Field label="Accepted forms" hint="Comma-separated.">
                      <input type="text" className="mono" value={editForms} onChange={(e) => setEditForms(e.target.value)} />
                    </Field>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-sm btn-primary" disabled={update.isPending}>
                      Save
                    </button>
                    <button type="button" className="btn btn-sm" onClick={() => setEditingId(null)}>
                      Cancel
                    </button>
                  </div>
                </form>
              </li>
            ) : (
              <li key={t.id} className="stack-item">
                <div className="stack-item-main">
                  <div className="stack-item-title">{t.word}</div>
                  <div className="forms-text">forms: {t.forms.join(", ")}</div>
                </div>
                <div className="stack-item-actions">
                  <button
                    type="button"
                    className="btn btn-sm"
                    onClick={() => {
                      setEditWord(t.word);
                      setEditForms(t.forms.join(", "));
                      setEditingId(t.id);
                    }}
                  >
                    Edit
                  </button>
                  <InlineConfirm label="Delete" onConfirm={() => remove.mutate(t.id)} busy={remove.isPending} />
                </div>
              </li>
            ),
          )}
        </ul>
      )}
      <form className="inline-form" onSubmit={onAdd}>
        <input
          type="text"
          value={word}
          placeholder="word the player must use"
          maxLength={60}
          required
          onChange={(e) => setWord(e.target.value)}
        />
        <button type="submit" className="btn btn-sm" disabled={create.isPending}>
          Add target word
        </button>
      </form>
      <MutationError error={create.error ?? update.error ?? remove.error} />
    </section>
  );
}
